import { ChevronRight } from 'lucide-react'
import type { InboxProjectRowDto } from '../../../../lib/api/v2/planning'
import { formatDuration } from '../work-item-slots'
import { cn } from '../../../../lib/utils'
import { formatRequestedInstall } from '../planning-helpers'
import { InterventionNatureBadge } from '../InterventionNatureBadge'

interface InboxListProps {
  rows: InboxProjectRowDto[]
  loading?: boolean
  /** Open the project detail drawer (the whole card is the trigger — no quick-schedule button). */
  onProjectClick?: (projectId: string) => void
}

const TYPE_LABEL: Record<string, string> = {
  cuisine: 'Cuisine', salle_de_bain: 'Salle de bain', placard: 'Dressing', salon: 'Salon', bureau: 'Bureau', autre: 'Autre',
}
const typeLabel = (t: string | null) => (t ? TYPE_LABEL[t] ?? t.charAt(0).toUpperCase() + t.slice(1) : 'Chantier')

/**
 * SOLY-REGIE-001 — what the card calls itself.
 *
 * An intervention carries no legacy project type (it is neither a kitchen nor a dressing), and the
 * fallback above is the literal word « Chantier ». Harmless while « chantier » only meant « a job »;
 * wrong the day it became the name of the OTHER nature — the card of a retouche was announcing
 * itself as the thing it exists not to be. Found in browser QA, 2026-08-19.
 */
const rowLabel = (row: InboxProjectRowDto) =>
  row.workType === 'Intervention' ? 'Intervention' : typeLabel(row.projectType)

/** Urgent = a requested install date that is past or within the 14-day window (header counter). */
function isUrgent(row: InboxProjectRowDto): boolean {
  const label = formatRequestedInstall(row.requestedInstallDate)
  return label !== null && label.urgency !== 'normal'
}

/**
 * One inbox card (SOLY-PLAN-001 MS-08, pose-driven). The whole card opens the project detail drawer —
 * planning happens inside it, after reviewing the project. The protected right-hand slot carries the
 * requested install date: it is the key the server sorts this very list by, so it must never be
 * truncated. Urgency is a colour, not a display condition. No badges, no quick-schedule button.
 */
function InboxCard({ row, onProjectClick }: { row: InboxProjectRowDto; onProjectClick?: (id: string) => void }) {
  const pose = formatRequestedInstall(row.requestedInstallDate)
  const loc = [row.postalCode, row.city].filter(Boolean).join(' ')
  return (
    <button
      type="button"
      data-testid={`planning-inbox-card-${row.projectId}`}
      onClick={() => onProjectClick?.(row.projectId)}
      className="group flex w-full items-center gap-2 rounded-lg border bg-card p-2.5 text-left shadow-sm transition hover:border-muted-foreground/40 hover:bg-muted/40"
    >
      <div className="min-w-0 flex-1">
        <span className="flex max-w-full items-center gap-1.5">
          <span className="min-w-0 truncate text-sm font-medium">{row.clientName}</span>
          {/* SOLY-FINITION-001 — nature badge (nothing for Initial). Server order is kept as-is. */}
          <InterventionNatureBadge nature={row.nature} />
        </span>
        <span className="block truncate text-xs text-muted-foreground">
          {rowLabel(row)}{loc && <> · {loc}</>}
        </span>
        {/* SOLY-PLAN-003 MS-09 — « partiellement planifiée »: some créneaux are placed, the budget
            is not spent. It is the SAME card and the SAME « Planifier » gesture on a chantier that
            already had one — no new section, no new tab (_design.md §3). Without this line the
            site taken apart waiting for the electrician (scénario S4) would leave the Inbox at the
            first créneau and nobody would ever finish planning it.
            The wording says what is LEFT, never « pas planifié » — two days are already booked. */}
        {row.plannedSlots > 0 && row.remainingHours > 0 && (
          <span className="block truncate text-[11px] text-muted-foreground"
            data-testid={`planning-inbox-remaining-${row.projectId}`}>
            Il reste {formatDuration(row.remainingHours, row.estimateUnit)} à planifier
          </span>
        )}
      </div>
      {pose && (
        <span
          className={cn(
            'flex-none text-[11px]',
            pose.urgency === 'overdue' && 'font-semibold text-red-600',
            pose.urgency === 'soon' && 'text-red-600',
            pose.urgency === 'normal' && 'text-muted-foreground',
          )}
          data-testid={`planning-inbox-date-${row.projectId}`}
        >
          {pose.text}
        </span>
      )}
      <ChevronRight className="h-4 w-4 flex-none text-muted-foreground/30 transition group-hover:text-muted-foreground/70" aria-hidden />
    </button>
  )
}

export function InboxList({ rows, loading, onProjectClick }: InboxListProps) {
  if (loading) {
    return <div className="p-3 text-sm text-muted-foreground" data-testid="planning-inbox-loading">Chargement…</div>
  }

  if (rows.length === 0) {
    return (
      <div className="p-4 text-center text-sm text-muted-foreground" data-testid="planning-inbox-empty">
        Aucune pose à planifier
      </div>
    )
  }

  const urgentCount = rows.filter(isUrgent).length
  return (
    <div className="flex h-full flex-col" data-testid="planning-inbox-list">
      <div className="flex items-baseline justify-between border-b px-3 py-2.5">
        <h2 className="text-sm font-semibold">À planifier <span className="font-normal text-muted-foreground">({rows.length})</span></h2>
        {urgentCount > 0 && <span className="text-xs font-medium text-red-600">{urgentCount} urgent{urgentCount > 1 ? 's' : ''}</span>}
      </div>
      <div className="flex-1 space-y-2 overflow-auto p-2.5">
        {rows.map((row) => (
          <InboxCard key={row.projectId} row={row} onProjectClick={onProjectClick} />
        ))}
      </div>
    </div>
  )
}
